/**
 * The builder's Layout choice, on the shared `LayoutPicker` — two tiles, each
 * a small wireframe of the respondent page above its radio + label:
 *
 *   all            the page with every question stacked, one Submit
 *   one_at_a_time  a progress bar, ONE question, Back / Next
 *
 * The wireframes are small portrait pages (all: three questions + Submit;
 * one at a time: stacked pages, step dots, one question, Back / Next arrows).
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { SURVEY_DISPLAY_MODES, type SurveyDisplayMode } from '@releve/shared';
import {
  LayoutPicker,
  LayoutWirePage,
  layoutWireLine as line,
  type LayoutPickerProps,
} from '@/components/ui/layout-picker';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

export type SurveyLayoutPickerProps = Omit<LayoutPickerProps<SurveyDisplayMode>, 'options'>;

const answerBox = 'h-3 rounded-sm border border-muted-foreground/50 bg-background';

/** A question: its label line and an answer box. */
function WireQuestion() {
  return (
    <div className="shrink-0 space-y-1">
      <div className={cn(line, 'w-4/5')} />
      <div className={answerBox} />
    </div>
  );
}

function LayoutWire({ mode }: { mode: SurveyDisplayMode }) {
  return mode === 'all' ? (
    <LayoutWirePage>
      <div className="h-2 w-3/5 shrink-0 rounded-full bg-foreground/60" />
      <WireQuestion />
      <WireQuestion />
      <WireQuestion />
      <div className="mt-auto h-3.5 w-11 shrink-0 self-end rounded-sm bg-primary" />
    </LayoutWirePage>
  ) : (
    <div className="relative">
      {/* The pages still to come, stacked behind the current one. */}
      <div className="absolute inset-0 translate-x-2 -translate-y-2 rounded-md border border-muted-foreground/30 bg-background" />
      <div className="absolute inset-0 translate-x-1 -translate-y-1 rounded-md border border-muted-foreground/30 bg-background" />
      <LayoutWirePage>
        <div className="flex gap-1">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className={cn('h-1.5 flex-1 rounded-full', i < 2 ? 'bg-primary' : 'bg-muted-foreground/25')} />
          ))}
        </div>
        <div className={cn(line, 'mt-1 h-2 w-4/5 bg-foreground/60')} />
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-1.5">
            <div className={cn('size-2.5 shrink-0 rounded-full border border-muted-foreground/60', i === 0 && 'border-primary bg-primary')} />
            <div className={cn(line, i === 1 ? 'w-1/2' : 'w-3/5')} />
          </div>
        ))}
        <div className="mt-auto flex shrink-0 justify-between">
          <div className="flex h-4 w-8 items-center justify-center rounded-sm border border-muted-foreground/50">
            <ChevronLeft className="size-3 text-muted-foreground" />
          </div>
          <div className="flex h-4 w-8 items-center justify-center rounded-sm bg-primary">
            <ChevronRight className="size-3 text-primary-foreground" />
          </div>
        </div>
      </LayoutWirePage>
    </div>
  );
}

/** Extra props (the `FormControl` slot's id / aria-describedby / aria-invalid) land on the group. */
export function SurveyLayoutPicker(props: Readonly<SurveyLayoutPickerProps>) {
  const { t } = useTranslation('surveys');
  const options = SURVEY_DISPLAY_MODES.map((mode) => ({
    value: mode,
    label: t(`dialog.displayModes.${mode}`),
    wire: <LayoutWire mode={mode} />,
  }));
  return <LayoutPicker {...props} options={options} />;
}
